import type { AccessContext, PermissionScope, PermissionSource } from './access-types';

export type RouteAccessDenial =
  | {
      reason: 'module_disabled';
      moduleKey: string;
      permissionKey: string;
    }
  | {
      reason: 'permission_missing';
      moduleKey: string;
      permissionKey: string;
      requiredScope: PermissionScope;
    };

const scopeRank: Record<PermissionScope, number> = {
  NONE: 0,
  OWNED: 1,
  OWNED_OR_ASSIGNED: 2,
  DEPARTMENT: 3,
  DEPARTMENT_AND_CHILDREN: 4,
  ALL: 5,
};

export function compareScopes(left: PermissionScope, right: PermissionScope) {
  return scopeRank[left] - scopeRank[right];
}

export function hasModule(context: AccessContext | null | undefined, moduleKey: string) {
  return Boolean(
    context?.enabledModules.some((module) => module.key === moduleKey && module.enabled),
  );
}

export function can(context: AccessContext | null | undefined, permissionKey: string) {
  return canWithScope(context, permissionKey, 'OWNED');
}

export function canWithScope(
  context: AccessContext | null | undefined,
  permissionKey: string,
  requiredScope: PermissionScope,
) {
  const permission = context?.effectivePermissions.find(
    (candidate) => candidate.key === permissionKey && !candidate.blockedByDisabledModule,
  );

  if (!permission || permission.effectiveScope === 'NONE') {
    return false;
  }

  if (permission.moduleKey && !hasModule(context, permission.moduleKey)) {
    return false;
  }

  return compareScopes(permission.effectiveScope, requiredScope) >= 0;
}

export function getEffectivePermissionScope(
  context: AccessContext | null | undefined,
  permissionKey: string,
): PermissionScope | null {
  const permission = context?.effectivePermissions.find(
    (candidate) => candidate.key === permissionKey && !candidate.blockedByDisabledModule,
  );

  if (!permission || permission.effectiveScope === 'NONE') {
    return null;
  }

  if (permission.moduleKey && !hasModule(context, permission.moduleKey)) {
    return null;
  }

  return permission.effectiveScope;
}

export function hasScopedPermission(
  context: AccessContext | null | undefined,
  permissionKey: string,
): boolean {
  const scope = getEffectivePermissionScope(context, permissionKey);
  return Boolean(scope && scope !== 'ALL');
}

export function getRouteAccessDenial(
  context: AccessContext | null | undefined,
  {
    moduleKey,
    permissionKey,
    requiredScope = 'OWNED',
  }: {
    moduleKey: string;
    permissionKey: string;
    requiredScope?: PermissionScope;
  },
): RouteAccessDenial | null {
  if (!hasModule(context, moduleKey)) {
    return {
      reason: 'module_disabled',
      moduleKey,
      permissionKey,
    };
  }

  if (!canWithScope(context, permissionKey, requiredScope)) {
    return {
      reason: 'permission_missing',
      moduleKey,
      permissionKey,
      requiredScope,
    };
  }

  return null;
}

export function getPermissionSources(
  context: AccessContext | null | undefined,
  permissionKey: string,
): PermissionSource[] {
  return (
    context?.configuredPermissions.find((permission) => permission.key === permissionKey)
      ?.sourceRoles ??
    context?.effectivePermissions.find((permission) => permission.key === permissionKey)
      ?.sourceRoles ??
    []
  );
}
